import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import type { CallJson } from 'app/types/entities'
import { gqlFetchOne } from 'app/lib/graphqlClient'
import { notifyError } from 'app/services/notify.client'
import { bg } from 'lib/background'
import { logError } from 'lib/logging'

type OutboundCallStatus = `polling` | `success` | `failed`

type OutboundCallState = {
  callId: string
  calledNumber?: string
  contactId?: string
  contactName?: string
  status: OutboundCallStatus
  call?: CallJson
  callStatus?: string
  error?: string
  lastUpdatedAt: string
}

type StartTrackingArgs = {
  callId: string
  calledNumber?: string
  contactId?: string
  contactName?: string
}

type OutboundCallContextValue = {
  activeCall: OutboundCallState | null
  startTrackingCall: (args: StartTrackingArgs) => void
  clearActiveCall: () => void
}

const FAST_INTERVAL_MS = 5000
const SLOW_INTERVAL_MS = 10000
const FAST_INTERVAL_THRESHOLD = 24
const MAX_ATTEMPTS = 120

const OutboundCallContext = createContext<OutboundCallContextValue | undefined>(undefined)

function deriveCallOutcome(call: CallJson | undefined): Exclude<OutboundCallStatus, `polling`> {
  if (!call) return `failed`

  const normalizedStatus = call.status?.toLowerCase() ?? ``
  if (normalizedStatus.includes(`fail`)) {
    return `failed`
  }

  return `success`
}

export function OutboundCallProvider({ children }: { children: React.ReactNode }) {
  const [activeCall, setActiveCall] = useState<OutboundCallState | null>(null)
  const attemptsRef = useRef(0)
  const timerRef = useRef<NodeJS.Timeout | null>(null)

  const stopTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }
  }, [])

  const clearActiveCall = useCallback(() => {
    stopTimer()
    attemptsRef.current = 0
    setActiveCall(null)
  }, [stopTimer])

  const startTrackingCall = useCallback(
    ({ callId, contactId, contactName, calledNumber }: StartTrackingArgs) => {
      if (!callId) return

      stopTimer()
      attemptsRef.current = 0
      setActiveCall({
        callId,
        contactId,
        contactName,
        calledNumber,
        status: `polling`,
        lastUpdatedAt: new Date().toISOString(),
      })
    },
    [stopTimer],
  )

  const callId = activeCall?.callId
  const status = activeCall?.status

  useEffect(() => {
    if (!callId || status !== `polling`) {
      return
    }

    let cancelled = false
    attemptsRef.current = 0

    function scheduleNextPoll() {
      if (cancelled) return
      const delay = attemptsRef.current >= FAST_INTERVAL_THRESHOLD ? SLOW_INTERVAL_MS : FAST_INTERVAL_MS
      stopTimer()
      timerRef.current = setTimeout(() => {
        if (!cancelled) {
          bg(async () => {
            await poll()
          })
        }
      }, delay)
    }

    async function poll() {
      if (cancelled) return

      attemptsRef.current += 1

      let callData: CallJson | undefined
      try {
        callData = await gqlFetchOne<CallJson>(
          'call',
          callId!,
          'id status duration direction callerPhone callerName createdDate',
        )
      } catch (error) {
        logError(`outbound-call.poll.fetch`, error)
        if (attemptsRef.current >= MAX_ATTEMPTS) {
          setActiveCall((prev) => {
            if (!prev || prev.callId !== callId) return prev
            const errorMessage = `Unable to verify the call status. Please check the call log for details.`
            notifyError(errorMessage)
            return {
              ...prev,
              status: `failed`,
              error: errorMessage,
              lastUpdatedAt: new Date().toISOString(),
            }
          })
          return
        }

        scheduleNextPoll()
        return
      }

      if (!callData) {
        if (attemptsRef.current >= MAX_ATTEMPTS) {
          setActiveCall((prev) => {
            if (!prev || prev.callId !== callId) return prev
            const errorMessage = `Call status check timed out. The call may not have been recorded yet.`
            notifyError(errorMessage)
            return {
              ...prev,
              status: `failed`,
              error: errorMessage,
              lastUpdatedAt: new Date().toISOString(),
            }
          })
          return
        }

        scheduleNextPoll()
        return
      }

      try {
        const payloadStatus: string = (callData as any)?.status ?? `unknown`
        const now = new Date().toISOString()

        if (payloadStatus !== `completed`) {
          if (attemptsRef.current >= MAX_ATTEMPTS) {
            const errorMessage = `Call status check timed out. The call may not have been recorded yet.`
            notifyError(errorMessage)
            setActiveCall((prev) => {
              if (!prev || prev.callId !== callId) return prev
              return {
                ...prev,
                status: `failed`,
                call: callData ?? prev.call,
                callStatus: payloadStatus,
                error: errorMessage,
                lastUpdatedAt: now,
              }
            })
            return
          }

          setActiveCall((prev) => {
            if (!prev || prev.callId !== callId) return prev
            return {
              ...prev,
              call: callData ?? prev.call,
              callStatus: payloadStatus,
              lastUpdatedAt: now,
            }
          })

          scheduleNextPoll()
          return
        }

        const result = deriveCallOutcome(callData)
        const isSuccess = result === `success`
        const failureMessage = !isSuccess
          ? (callData as any)?.error || `We couldn’t confirm this call. Please check the call log for details.`
          : undefined

        if (failureMessage) {
          notifyError(failureMessage)
        }

        stopTimer()
        setActiveCall((prev) =>
          prev && prev.callId === callId
            ? {
                ...prev,
                status: result,
                call: callData ?? prev.call,
                callStatus: payloadStatus,
                error: isSuccess ? undefined : failureMessage,
                lastUpdatedAt: now,
              }
            : prev,
        )
      } catch (error) {
        logError(`outbound-call.poll.json`, error)
        const errorMessage = `Received an unexpected response while checking the call status.`
        notifyError(errorMessage)
        setActiveCall((prev) => {
          if (!prev || prev.callId !== callId) return prev
          return {
            ...prev,
            status: `failed`,
            error: errorMessage,
            lastUpdatedAt: new Date().toISOString(),
          }
        })
      }
    }

    bg(async () => {
      await poll()
    })

    return () => {
      cancelled = true
      stopTimer()
    }
  }, [callId, status, stopTimer])

  const value = useMemo(
    () => ({
      activeCall,
      startTrackingCall,
      clearActiveCall,
    }),
    [activeCall, startTrackingCall, clearActiveCall],
  )

  return <OutboundCallContext.Provider value={value}>{children}</OutboundCallContext.Provider>
}

const noop = () => {}
const defaultValue: OutboundCallContextValue = {
  activeCall: null,
  startTrackingCall: noop,
  clearActiveCall: noop,
}

export function useOutboundCall(): OutboundCallContextValue {
  return useContext(OutboundCallContext) ?? defaultValue
}
